import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { TagShopProduct } from './useTagProducts'
import { TAG_SHOP_MOCK_ENABLED, getMockTagProduct } from 'mocks/tagShopMock'

type UseTagProductParams = {
  /** Product SKU (the representative first-variant SKU used for routing). */
  sku: string;
}

type UseTagProductResult = {
  data: TagShopProduct | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

// Defensive pass-through — the /shop/tags/products/:sku endpoint already returns
// the exact shape the UI expects (best_for_product_types[], per-variant quantity + unit_price
// computed from the SKU server-side). Trust the server values.
function transformProduct(product: any): TagShopProduct {
  return {
    id: product.id,
    sku: product.sku,
    title: product.title,
    images: product.images || [],
    best_for_product_types: Array.isArray(product.best_for_product_types)
      ? product.best_for_product_types
      : product.best_for_product_types
        ? [product.best_for_product_types]
        : [],
    shopify_category_id: product.shopify_category_id ?? null,
    category_type: product.category_type ?? null,
    description: product.description ?? null,
    details: product.details ?? null,
    width: product.width ?? null,
    height: product.height ?? null,
    depth: product.depth ?? null,
    shape: product.shape ?? null,
    sides: product.sides ?? null,
    // VRG-141/143: capability model surfaced from the server (real Shopify path).
    capabilities: Array.isArray(product.capabilities) ? product.capabilities : null,
    form_factor: product.form_factor ?? null,
    on_metal_available: product.on_metal_available ?? null,
    shapes: Array.isArray(product.shapes) ? product.shapes : null,
    durability: Array.isArray(product.durability) ? product.durability : null,
    embroidery: Array.isArray(product.embroidery) ? product.embroidery : null,
    cardMaterial: Array.isArray(product.cardMaterial) ? product.cardMaterial : null,
    unit_price: Number(product.unit_price) || 0,
    variants: (product.variants || []).map((variant: any) => ({
      id: variant.id,
      sku: variant.sku ?? null,
      title: variant.title,
      quantity: Number(variant.quantity) || 0,
      price: Number(variant.price) || 0,
      unit_price: Number(variant.unit_price) || 0,
      images: Array.isArray(variant.images)
        ? variant.images
        : variant.image
          ? [variant.image]
          : [],
      capability: variant.capability ?? null,
      size: variant.size ?? null,
      tag_family: variant.tag_family ?? null,
    })),
  };
}

export const useTagProduct = (
  params: UseTagProductParams,
  options?: { enabled?: boolean }
): UseTagProductResult => {
  const { sku } = params;
  const isEnabled = options?.enabled !== false && !!sku;

  const query = useQuery({
    queryKey: ['tag-shop-products', 'detail', sku],
    queryFn: async () => {
      // DEV-only mock (VITE_TAG_SHOP_MOCK).
      if (TAG_SHOP_MOCK_ENABLED) {
        return getMockTagProduct(sku) ?? null
      }

      const response = await createApiCall({
        method: 'GET',
        url: `/shop/tags/products/${encodeURIComponent(sku)}`,
      });

      // Endpoint returns { product: TagShopProductDto }
      return transformProduct(response.data.product ?? response.data);
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 3, // 3 minutes (matches cache TTL)
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
  });

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
